import React, { useState, useEffect, useCallback, useRef } from 'react'

export default function Carrossel({ fotos, tenantNome }) {
  const [atual, setAtual] = useState(0)
  const [arrastando, setArrastando] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const inicioX = useRef(null)
  const total = fotos.length

  // Deteta mobile (<=768px) — em mobile escondemos as setas e as faixas laterais
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)')
    const atualizar = () => setIsMobile(mq.matches)
    atualizar()
    mq.addEventListener('change', atualizar)
    return () => mq.removeEventListener('change', atualizar)
  }, [])

  const anterior = useCallback(() => {
    setAtual(i => (i - 1 + total) % total)
  }, [total])

  const proximo = useCallback(() => {
    setAtual(i => (i + 1) % total)
  }, [total])

  useEffect(() => {
    if (total <= 1) return
    const id = setInterval(proximo, 5000)
    return () => clearInterval(id)
  }, [proximo, total])

  function onPointerDown(e) {
    inicioX.current = e.clientX ?? e.touches?.[0]?.clientX
    setArrastando(true)
  }
  function onPointerUp(e) {
    if (!arrastando || inicioX.current === null) return
    const fimX = e.clientX ?? e.changedTouches?.[0]?.clientX
    const delta = fimX - inicioX.current
    if (Math.abs(delta) > 50) delta < 0 ? proximo() : anterior()
    inicioX.current = null
    setArrastando(false)
  }

  if (!fotos.length) return null

  // Setas só em desktop (em mobile a navegação é por swipe + dots)
  const mostrarSetas = total > 1 && !isMobile

  return (
    <section
      aria-label="Galeria de fotografias"
      style={{
        background: 'linear-gradient(135deg, var(--cor-principal) 0%, var(--cor-principal-escuro) 100%)',
        userSelect: 'none',
      }}
    >
      {/* Área da foto — 16:9 em desktop, altura fixa em mobile */}
      <div
        style={{
          position: 'relative',
          overflow: 'hidden',
          aspectRatio: isMobile ? 'auto' : '16/9',
          height: isMobile ? 'clamp(420px, 70vh, 560px)' : 'auto',
        }}
        onMouseDown={onPointerDown}
        onMouseUp={onPointerUp}
        onTouchStart={onPointerDown}
        onTouchEnd={onPointerUp}
      >
        {fotos.map((foto, i) => (
          <img
            key={i}
            src={foto.src}
            alt={foto.alt}
            draggable={false}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'center',
              opacity: i === atual ? 1 : 0,
              transition: 'opacity 0.6s ease',
              pointerEvents: 'none',
            }}
          />
        ))}

        {/* Label no topo */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          padding: 'clamp(20px, 5vw, 32px) 24px 0',
          textAlign: 'center',
          zIndex: 3,
          pointerEvents: 'none',
        }}>
          <span style={{
            display: 'inline-block',
            color: 'rgba(255,255,255,0.85)',
            fontFamily: 'var(--font-ui)',
            fontSize: '11px',
            fontWeight: '500',
            letterSpacing: '0.3em',
            textTransform: 'uppercase',
            textShadow: '0 1px 6px rgba(0,0,0,0.4)',
            animation: 'fadeUp 1s var(--ease) 0.1s both',
          }}>
            Clínica de Cuidado Integrado
          </span>
        </div>

        {/* Gradiente inferior — reforçado para legibilidade do título/botão */}
        <div style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '55%',
          background: 'linear-gradient(to top, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.35) 45%, transparent 100%)',
          pointerEvents: 'none',
        }} />

        {/* Setas de navegação — apenas desktop */}
        {mostrarSetas && (
          <>
            <button onClick={anterior} aria-label="Foto anterior" style={estiloSeta('left')}>‹</button>
            <button onClick={proximo} aria-label="Próxima foto" style={estiloSeta('right')}>›</button>
          </>
        )}

        {/* Conteúdo inferior: nome + botão + dots */}
        <div style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px',
          paddingBottom: 'max(30px, env(safe-area-inset-bottom))',
          zIndex: 3,
        }}>
          <img
            src="/fotos/Titulo.png"
            alt={tenantNome || 'Título'}
            style={{
              width: isMobile ? 'clamp(300px, 92vw, 520px)' : 'clamp(560px, 65vw, 1000px)',
              height: 'auto',
              objectFit: 'contain',
              filter: 'drop-shadow(0 2px 14px rgba(0,0,0,0.5))',
              animation: 'fadeUp 1s var(--ease) 0.25s both',
            }}
          />

          <a
            href="/agendar"
            style={{
              display: 'inline-block',
              background: 'white',
              color: 'var(--cor-principal)',
              padding: '14px 40px',
              borderRadius: '50px',
              textDecoration: 'none',
              fontFamily: 'var(--font-ui)',
              fontWeight: '600',
              fontSize: '15px',
              letterSpacing: '0.03em',
              boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
              whiteSpace: 'nowrap',
              transition: 'opacity 0.2s',
              animation: 'fadeUp 1s var(--ease) 0.4s both',
            }}
          >
            Agendar Tratamento
          </a>

          {/* Dots — área de toque alargada (>=24px) sem mudar o visual */}
          {total > 1 && (
            <div style={{ display: 'flex', gap: '4px' }}>
              {fotos.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setAtual(i)}
                  aria-label={`Foto ${i + 1}`}
                  style={{
                    width: '24px',
                    height: '24px',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <span style={{
                    display: 'block',
                    width: i === atual ? '22px' : '8px',
                    height: '8px',
                    borderRadius: '4px',
                    background: i === atual ? 'white' : 'rgba(255,255,255,0.45)',
                    transition: 'width 0.3s, background 0.3s',
                  }} />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

function estiloSeta(lado) {
  return {
    position: 'absolute',
    top: '50%',
    [lado]: '12px',
    transform: 'translateY(-50%)',
    zIndex: 4,
    background: 'rgba(255,255,255,0.18)',
    border: 'none',
    color: 'white',
    fontSize: '28px',
    lineHeight: 1,
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backdropFilter: 'blur(4px)',
    transition: 'background 0.2s',
  }
}
